import imageCompression from 'browser-image-compression';
import React, { useEffect, useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';

import { getMainDomain } from '../utils/component-utils';
import { showTokenModal } from './TokenModal';

interface FileSelectorModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (fileUrl: string) => void;
  onSelectMultiple?: (fileUrls: string[]) => void;
  allowMultipleSelection?: boolean;
  configParam: string;
  handleUpload: (blob: Blob, filename: string) => Promise<boolean>;
}

const FileSelectorModal: React.FC<FileSelectorModalProps> = ({
  open,
  onClose,
  onSelect,
  onSelectMultiple,
  allowMultipleSelection = false,
  configParam,
  handleUpload,
}) => {
  const [files, setFiles] = useState<string[]>([]);
  const [deletingFile, setDeletingFile] = useState<string | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);

  // Reset selected files when modal opens/closes
  useEffect(() => {
    if (open) {
      setSelectedFiles([]);
    }
  }, [open]);

  useEffect(() => {
    const fetchFiles = async () => {
      if (configParam === 'localhost') {
        // For local development, use empty array
        setFiles([]);
        return;
      }
      const mainDomain = getMainDomain();

      const fetchUrl = `https://upload.${mainDomain}/files/${configParam}`;

      try {
        // Get auth token
        const authToken = await showTokenModal();
        if (!authToken) {
          console.log(
            'File listing cancelled - no authentication token provided'
          );
          return;
        }

        const response = await fetch(fetchUrl, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
          method: 'GET',
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Sort files by modification time (newest first)
        const filesWithTimestamp = await Promise.all(
          data.map(async (fileUrl: string) => {
            try {
              const fileResponse = await fetch(
                '/site_data/' + fileUrl.split('/').slice(-2).join('/'),
                {
                  method: 'HEAD',
                }
              );
              const lastModified = fileResponse.headers.get('Last-Modified');
              return {
                url: fileUrl,
                timestamp: lastModified ? new Date(lastModified).getTime() : 0,
              };
            } catch {
              return { url: fileUrl, timestamp: 0 };
            }
          })
        );

        const sortedFiles = filesWithTimestamp
          .sort((a, b) => b.timestamp - a.timestamp)
          .map((item) => item.url);

        setFiles(sortedFiles);
      } catch (error) {
        console.error('Error fetching files:', error);
      }
    };

    if (open) {
      fetchFiles();
    }
  }, [open, configParam]);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = event.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      // Convert FileList to Array for easier handling
      const filesArray = Array.from(selectedFiles);

      // Start uploading all selected files
      await uploadMultipleFiles(filesArray);

      // Clear the input
      event.target.value = '';
    }
  };

  const uploadMultipleFiles = async (selectedFiles: File[]) => {
    const fileNames = selectedFiles.map((file) => file.name);
    setUploadingFiles(fileNames);

    try {
      // Process all files in parallel
      const uploadPromises = selectedFiles.map(async (file) => {
        try {
          let blob: Blob;
          let fileName: string;

          // Check if file is an image and not a GIF (to preserve animations)
          const isImage = file.type.startsWith('image/');
          const isGif = file.type === 'image/gif';

          if (isImage && !isGif) {
            // Compress images except GIFs
            const options = {
              maxSizeMB: 1,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
            };
            const compressedFile = await imageCompression(file, options);
            blob = new Blob([compressedFile], { type: compressedFile.type });
            fileName = compressedFile.name.replace(/ /g, '_');
          } else {
            // Don't compress GIFs, videos and other files
            blob = new Blob([file], { type: file.type });
            fileName = file.name.replace(/ /g, '_');
          }

          const success = await handleUpload(blob, fileName);
          if (success) {
            return `./site_data/${configParam}/${fileName}`;
          }
          return null;
        } catch (error) {
          console.error(`Error processing file ${file.name}:`, error);
          return null;
        }
      });

      const uploadResults = await Promise.all(uploadPromises);
      const successfulUploads = uploadResults.filter(
        (result) => result !== null
      );

      // If we have successful uploads, select the first one and refresh the file list
      if (successfulUploads.length > 0) {
        // Add new files to the existing list and sort by newest first
        const newFiles = [...(successfulUploads as string[]), ...files];
        const uniqueFiles = Array.from(new Set(newFiles));
        setFiles(uniqueFiles);

        // Select the first uploaded file
        onSelect(successfulUploads[0] as string);
      }
    } finally {
      setUploadingFiles([]);
    }
  };

  const handleDeleteFile = async (fileUrl: string) => {
    if (configParam === 'localhost') {
      console.log('File deletion not supported in localhost mode');
      return;
    }

    try {
      setDeletingFile(fileUrl);

      // Get auth token
      const authToken = await showTokenModal();
      if (!authToken) {
        console.log(
          'File deletion cancelled - no authentication token provided'
        );
        setDeletingFile(null);
        return;
      }

      // Extract filename from URL
      const fileName = fileUrl.split('/').pop();
      const mainDomain = getMainDomain();
      const deleteUrl = `https://upload.${mainDomain}/upload/${configParam}/${fileName}`;

      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Remove file from local state
      setFiles((prevFiles) => prevFiles.filter((file) => file !== fileUrl));
      console.log('File deleted successfully');
    } catch (error) {
      console.error('Error deleting file:', error);
    } finally {
      setDeletingFile(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Select or Upload Image</DialogTitle>
      <DialogContent>
        <Box mt={2}>
          <input
            type="file"
            multiple
            onChange={handleFileChange}
            accept="image/*,video/*"
            style={{ marginBottom: '8px' }}
          />
          {uploadingFiles.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <div>Uploading {uploadingFiles.length} file(s)...</div>
              {uploadingFiles.map((fileName, index) => (
                <div key={index} style={{ fontSize: '0.9em', color: 'gray' }}>
                  • {fileName}
                </div>
              ))}
            </Box>
          )}
        </Box>
        <List>
          {files.map((file, index) => (
            <ListItem
              key={index}
              component="div"
              secondaryAction={
                configParam !== 'localhost' && (
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteFile(file);
                    }}
                    disabled={deletingFile === file}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                )
              }
            >
              <ListItemButton
                onClick={() => {
                  if (allowMultipleSelection) {
                    const isSelected = selectedFiles.includes(file);
                    if (isSelected) {
                      setSelectedFiles(selectedFiles.filter((f) => f !== file));
                    } else {
                      setSelectedFiles([...selectedFiles, file]);
                    }
                  } else {
                    onSelect(file);
                  }
                }}
                sx={{
                  backgroundColor:
                    allowMultipleSelection && selectedFiles.includes(file)
                      ? 'rgba(25, 118, 210, 0.12)'
                      : 'transparent',
                }}
              >
                {allowMultipleSelection && (
                  <input
                    type="checkbox"
                    checked={selectedFiles.includes(file)}
                    onChange={() => {}} // Handled by parent onClick
                    style={{ marginRight: '8px' }}
                  />
                )}
                <img
                  src={'/site_data/' + file.split('/').slice(-2).join('/')}
                  alt={`thumbnail-${index}`}
                  style={{ width: '50px', height: '50px', marginRight: '10px' }}
                />
                <ListItemText primary={file.split('/').pop()} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {allowMultipleSelection && (
          <>
            <Button
              onClick={() => setSelectedFiles([])}
              disabled={selectedFiles.length === 0}
            >
              Clear Selection
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                if (onSelectMultiple && selectedFiles.length > 0) {
                  onSelectMultiple(selectedFiles);
                }
              }}
              disabled={selectedFiles.length === 0}
            >
              Select {selectedFiles.length} Image
              {selectedFiles.length !== 1 ? 's' : ''}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FileSelectorModal;
